import type { ConsentPreferences } from '@/lib/marketing/consent';

export const ATTRIBUTION_STORAGE_KEY = 'printen_attribution';

type AttributionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type Gtag = (...args: unknown[]) => void;

export type GaBrowserIdentifiers = {
  gaClientId?: string;
  gaSessionId?: string;
};

function safeDecode(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function validClientId(value: unknown): value is string {
  return typeof value === 'string' && /^\d+\.\d+$/.test(value);
}

function validSessionId(value: unknown): value is string {
  return typeof value === 'string' && /^\d+$/.test(value);
}

/** Parse both GA's legacy GS1 and current GS2 cookie formats. */
export function readGaCookieIdentifiers(cookieHeader: string, measurementId?: string): GaBrowserIdentifiers {
  let gaClientId: string | undefined;
  let gaSessionId: string | undefined;

  for (const part of String(cookieHeader || '').split(';')) {
    const separator = part.indexOf('=');
    if (separator <= 0) continue;
    const name = part.slice(0, separator).trim();
    const value = safeDecode(part.slice(separator + 1).trim());

    if (name === '_ga') {
      const candidate = value.match(/^GA\d+\.\d+\.(\d+\.\d+)$/)?.[1];
      if (validClientId(candidate)) gaClientId = candidate;
      continue;
    }

    if (!name.startsWith('_ga_')) continue;
    if (measurementId !== undefined && name !== `_ga_${measurementId.replace(/^G-/i, '')}`) continue;
    const legacy = value.match(/^GS\d+\.\d+\.(\d+)/)?.[1];
    const current = value.match(/^GS\d+\.\d+\.s(\d+)(?:\$|$)/)?.[1];
    const candidate = current || legacy;
    if (validSessionId(candidate)) gaSessionId = candidate;
  }

  return { gaClientId, gaSessionId };
}

function readAttribution(storage: AttributionStorage): Record<string, unknown> {
  try {
    const value = JSON.parse(storage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

export function attributionAllowedByConsent(
  attribution: Record<string, unknown>,
  consent: ConsentPreferences,
) {
  if (!consent.analytics && !consent.marketing) return {};
  const allowed = { ...attribution };
  if (!consent.marketing) { delete allowed.oppref; delete allowed.obref; delete allowed.ttclid; delete allowed.ttp; }
  if (!consent.analytics) {
    delete allowed.gaClientId;
    delete allowed.gaSessionId;
  }
  return allowed;
}

function getGtagValue(
  gtag: Gtag,
  measurementId: string,
  field: 'client_id' | 'session_id',
  timeoutMs: number,
): Promise<string | undefined> {
  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (value?: unknown) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      const normalized = String(value ?? '').trim();
      resolve(normalized || undefined);
    };
    timer = setTimeout(() => finish(), timeoutMs);
    try {
      gtag('get', measurementId, field, finish);
    } catch {
      finish();
    }
  });
}

/**
 * Take a fresh, consent-gated attribution snapshot. Cookie parsing is instant;
 * gtag's `get` callback closes the race where config has been queued but GA has
 * not written its cookies yet.
 */
export async function captureGaAttributionSnapshot(input: {
  consent: ConsentPreferences;
  storage: AttributionStorage;
  cookieHeader: string;
  gtag?: Gtag;
  measurementId?: string;
  timeoutMs?: number;
  getCurrentConsent?: () => ConsentPreferences;
}) {
  const existing = readAttribution(input.storage);
  const allowed = attributionAllowedByConsent(existing, input.consent);

  if (!input.consent.analytics && !input.consent.marketing) {
    input.storage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return {};
  }
  if (!input.consent.analytics) {
    input.storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(allowed));
    return allowed;
  }

  const measurementId = String(input.measurementId || '').trim();
  const cookieIds = readGaCookieIdentifiers(input.cookieHeader, input.measurementId === undefined ? undefined : measurementId);
  let gaClientId = cookieIds.gaClientId;
  let gaSessionId = cookieIds.gaSessionId;

  if ((!gaClientId || !gaSessionId) && input.gtag && /^G-[A-Z0-9-]+$/i.test(measurementId)) {
    const timeoutMs = Math.max(0, input.timeoutMs ?? 750);
    const [clientId, sessionId] = await Promise.all([
      gaClientId ? undefined : getGtagValue(input.gtag, measurementId, 'client_id', timeoutMs),
      gaSessionId ? undefined : getGtagValue(input.gtag, measurementId, 'session_id', timeoutMs),
    ]);
    if (validClientId(clientId)) gaClientId = clientId;
    if (validSessionId(sessionId)) gaSessionId = sessionId;
  }

  // gtag callbacks can arrive after navigation or consent withdrawal. Merge
  // into the latest attribution and never resurrect withdrawn identifiers.
  const currentConsent = input.getCurrentConsent?.() ?? input.consent;
  if (!currentConsent.analytics && !currentConsent.marketing) {
    input.storage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return {};
  }
  const current = attributionAllowedByConsent(readAttribution(input.storage), currentConsent);
  const snapshot = {
    ...current,
    ...(currentConsent.analytics ? {
    ...(gaClientId ? { gaClientId } : {}),
    ...(gaSessionId ? { gaSessionId } : {}),
    } : {}),
  };
  input.storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(snapshot));
  return snapshot;
}
